// Vocabulary Flashcards — flip cards, mark known / still learning; tougher cards
// (more "still learning" marks) surface first. Ported from the legacy
// views.flashcards, then re-tuned for Verde's calm, no-gamification feel:
//   • no persisted streaks (session-only in-memory stats, resets on reload),
//   • no score celebration on completion (a quiet "you went through the deck"),
//   • no per-card running tally on screen.
// The "Still learning / I knew it" self-rating stays — it's an honest recall
// signal (and the hook the Leitner review utility reads), not a points game.
import { VOCABULARY, TOP_50_VERBS, ALL_VERBS } from "../data/index.js";
import { VOCABULARY_CARDS } from "../data/vocabulary.js";
import { escapeHtml } from "../ui/helpers.js";
import { speakButtonHTML } from "../lib/audio.js";
import { fetchPersonalVocab, recordPersonalReview, addPersonalWord } from "../lib/personalVocab.js";
import { isAuthed } from "../lib/auth.js";
import { generateVocab, aiErrorMessage } from "../lib/ai.js";

// Quick-pick themes for the AI deck — a beginner can just tap one.
const AI_THEMES = [
  "restaurant", "travel", "shopping", "directions", "family", "at the doctor",
  "work", "the weather", "food & drink", "hobbies", "daily routine", "numbers & time",
];

// Session-only per-card stats, keyed by card.key. Resets on reload.
const flashStats = {};
let flashState = null;

function buildDeck(deck, dir, personalRows) {
  let cards;
  if (deck === "personal") {
    // Harvested words: Spanish lemma on the front, English on the back, the
    // source sentence as quiet context. _personal/_id drive SRS persistence.
    cards = (personalRows || []).map((r) => ({
      front: r.es,
      back: r.word,
      key: `pv:${r.id}`,
      _id: r.id,
      _personal: true,
      due: r.srs_due_at || "",
      context: r.sentence ? `From: ${r.sentence}` : "",
    }));
  } else if (deck === "vocab-rich") {
    cards = VOCABULARY_CARDS.map((v) => ({
      front: v.spanish,
      back: v.english,
      key: `vc:${v.spanish}`,
      emoji: v.emoji || "",
      exEs: v.example_es || "",
      exEn: v.example_en || "",
    }));
  } else if (deck === "vocab") {
    cards = VOCABULARY.map((v) => ({ front: v.es, back: v.en, key: v.es }));
  } else if (deck === "top50") {
    cards = TOP_50_VERBS.map((v) => ({ front: v.inf, back: v.en, key: v.inf, emoji: v.emoji || "" }));
  } else {
    cards = ALL_VERBS.map((v) => ({ front: v.inf, back: v.en, key: v.inf }));
  }
  if (dir === "en-es") {
    // Flip face/translation but keep example sentences + metadata intact.
    cards = cards.map((c) => ({ ...c, front: c.back, back: c.front, frontIsEnglish: true }));
  }
  return cards;
}

// New cards (no schedule yet) sort first; otherwise earliest-due first.
function dueTime(card) {
  return card.due ? new Date(card.due).getTime() : 0;
}

export function flashcardsView(app, params = {}) {
  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Vocabulary Flashcards</h2>
      <p class="text-sage-600 text-sm mb-6">Tap the card to flip. Mark each as known or still learning — tougher cards come back sooner. No scores kept.</p>

      <div class="bg-white rounded-2xl border border-sage-100 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <label class="text-sm flex items-center gap-2">
          <span class="font-medium text-sage-700">Deck:</span>
          <select id="deck-sel" class="border border-sage-200 rounded-lg px-2 py-1 text-sm bg-white">
            <option value="ai-theme">✨ Themed set (AI) — any topic</option>
            <option value="personal">My words (your saved deck)</option>
            <option value="top50">Top 50 verbs</option>
            <option value="all-verbs">All verbs</option>
          </select>
        </label>
        <label class="text-sm flex items-center gap-2">
          <span class="font-medium text-sage-700">Direction:</span>
          <select id="dir-sel" class="border border-sage-200 rounded-lg px-2 py-1 text-sm bg-white">
            <option value="es-en">Spanish → English</option>
            <option value="en-es">English → Spanish</option>
          </select>
        </label>
        <button id="flash-start" class="ml-auto bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Start</button>
      </div>

      <div id="ai-theme-row" class="hidden bg-white rounded-2xl border border-sage-100 p-4 mb-4">
        <div class="flex flex-wrap gap-3 items-center mb-2">
          <label class="text-sm flex items-center gap-2 flex-1 min-w-[200px]">
            <span class="font-medium text-sage-700">Theme:</span>
            <input id="ai-theme-input" type="text" placeholder="e.g. ordering at a restaurant" class="flex-1 border border-sage-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
          </label>
          <label class="text-sm flex items-center gap-2">
            <span class="font-medium text-sage-700">Level:</span>
            <select id="ai-tier-sel" class="border border-sage-200 rounded-lg px-2 py-1 text-sm bg-white">
              ${["A1", "A2", "B1", "B2", "C1", "C2"].map((t) => `<option value="${t}"${t === "A1" ? " selected" : ""}>${t}</option>`).join("")}
            </select>
          </label>
        </div>
        <div class="flex flex-wrap gap-1.5">
          ${AI_THEMES.map((t) => `<button type="button" data-theme="${escapeHtml(t)}" class="theme-chip px-2.5 py-1 rounded-full text-xs border border-sage-200 text-sage-600 hover:border-sage-400 bg-white">${escapeHtml(t)}</button>`).join("")}
        </div>
        <p class="text-xs text-sage-400 mt-2">A fresh set each time · counts toward your daily AI limit. Tap “✗ Still learning” on a card to save it to <b>My words</b> for review.</p>
      </div>

      <div id="flash-area"></div>
    </section>
  `;

  // Show the theme/level row only for the AI deck.
  const deckSel = app.querySelector("#deck-sel");
  const aiRow = app.querySelector("#ai-theme-row");
  const syncAiRow = () => aiRow.classList.toggle("hidden", deckSel.value !== "ai-theme");
  deckSel.addEventListener("change", syncAiRow);
  syncAiRow();
  // Theme chips fill the input.
  app.querySelectorAll(".theme-chip").forEach((chip) =>
    chip.addEventListener("click", () => {
      app.querySelector("#ai-theme-input").value = chip.dataset.theme;
    })
  );
  app.querySelector("#flash-start").addEventListener("click", async () => {
    const deck = app.querySelector("#deck-sel").value;
    const dir = app.querySelector("#dir-sel").value;
    const startBtn = app.querySelector("#flash-start");
    const area = app.querySelector("#flash-area");
    const errBox = (m) => `<div class="bg-white rounded-2xl border border-rose-100 p-5 text-sm text-rose-600">${escapeHtml(m)}</div>`;

    // AI themed set: generate a fresh deck for the typed theme.
    if (deck === "ai-theme") {
      if (!isAuthed()) {
        area.innerHTML = errBox("Sign in to generate a themed AI set.");
        return;
      }
      const theme = (app.querySelector("#ai-theme-input").value || "").trim();
      const tier = app.querySelector("#ai-tier-sel").value || "A1";
      if (!theme) {
        area.innerHTML = errBox("Type a theme — e.g. “ordering at a restaurant”.");
        return;
      }
      startBtn.disabled = true;
      startBtn.textContent = "Generating…";
      try {
        const res = await generateVocab({ theme, tier });
        let cards = (res.cards || []).map((c) => ({
          front: c.es,
          back: c.en,
          key: `ai:${c.es}`,
          // Original es/en kept regardless of display direction, so a saved card
          // always lands in My words correctly.
          _es: c.es,
          _en: c.en,
          _pos: c.pos || "",
          _theme: theme,
          _tier: tier,
          _aiSave: true,
        }));
        if (dir === "en-es") cards = cards.map((c) => ({ ...c, front: c.back, back: c.front, frontIsEnglish: true }));
        startBtn.disabled = false;
        startBtn.textContent = "Start";
        if (!cards.length) {
          area.innerHTML = errBox("Couldn't build that set — try another theme.");
          return;
        }
        flashState = { cards, idx: 0, flipped: false };
        renderFlash(app);
      } catch (err) {
        startBtn.disabled = false;
        startBtn.textContent = "Start";
        area.innerHTML = errBox(aiErrorMessage(err));
      }
      return;
    }

    let personalRows = [];
    if (deck === "personal") {
      startBtn.disabled = true;
      startBtn.textContent = "Loading…";
      personalRows = await fetchPersonalVocab();
      startBtn.disabled = false;
      startBtn.textContent = "Start";
    }

    const cards = buildDeck(deck, dir, personalRows);

    if (deck === "personal" && cards.length === 0) {
      app.querySelector("#flash-area").innerHTML = `
        <div class="bg-white rounded-2xl border border-dashed border-sage-200 p-8 text-center">
          <div class="text-4xl mb-3">🗂️</div>
          <h3 class="text-lg font-bold text-sage-800 mb-1">No personal words yet</h3>
          <p class="text-sage-600 text-sm max-w-md mx-auto">Open <b>AI Translate</b>, and when you reveal a Reference Card for a word you don't know, it's saved here as a flashcard. (You'll need to be signed in.)</p>
        </div>`;
      return;
    }

    if (deck === "personal") {
      // Anki order: brand-new and earliest-due cards lead.
      cards.sort((a, b) => dueTime(a) - dueTime(b));
    } else {
      // Session weighting: "still learning" cards lead (resets on reload).
      cards.sort((a, b) => {
        const sa = (flashStats[a.key]?.correct || 0) - (flashStats[a.key]?.wrong || 0);
        const sb = (flashStats[b.key]?.correct || 0) - (flashStats[b.key]?.wrong || 0);
        return sa - sb;
      });
    }
    flashState = { cards, idx: 0, flipped: false };
    renderFlash(app);
  });

  // Deep link (e.g. from Refine's "Review words") can pre-pick a deck and start.
  if (params.deck) {
    const ds = app.querySelector("#deck-sel");
    if (ds && [...ds.options].some((o) => o.value === params.deck)) {
      ds.value = params.deck;
      app.querySelector("#flash-start").click();
    }
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
}

function faceHTML(text, emoji) {
  const e = emoji ? `<span class="text-4xl mb-2" aria-hidden="true">${emoji}</span>` : "";
  return `<div class="flex flex-col items-center justify-center text-center">${e}<p class="text-3xl sm:text-4xl font-bold text-sage-800">${escapeHtml(text)}</p></div>`;
}

function backHTML(card) {
  const e = card.emoji ? `<span class="text-3xl mb-1" aria-hidden="true">${card.emoji}</span>` : "";
  // Show the Spanish example under the answer when this deck carries one.
  const ex = card.exEs
    ? `<div class="mt-3 text-center">
         <p class="text-sm text-sage-700">${escapeHtml(card.exEs)}</p>
         ${card.exEn ? `<p class="text-xs text-sage-500 italic mt-0.5">${escapeHtml(card.exEn)}</p>` : ""}
       </div>`
    : "";
  const ctx = card.context
    ? `<p class="mt-3 text-xs text-sage-500 italic">${escapeHtml(card.context)}</p>`
    : "";
  return `<div class="flex flex-col items-center justify-center text-center w-full px-2">${e}<p class="text-3xl sm:text-4xl font-bold text-sage-700">${escapeHtml(card.back)}</p>${ex}${ctx}</div>`;
}

function renderFlash(app) {
  const fs = flashState;
  const area = app.querySelector("#flash-area");
  if (fs.idx >= fs.cards.length) {
    const isAi = fs.cards.some((c) => c._aiSave);
    area.innerHTML = `
      <div class="bg-white rounded-2xl border border-sage-100 p-8 text-center">
        <div class="text-4xl mb-3">🌿</div>
        <h3 class="text-xl font-bold text-sage-800 mb-1">You went through the deck</h3>
        <p class="text-sage-600 mb-5 text-sm">${fs.cards.length} card${fs.cards.length === 1 ? "" : "s"} reviewed.${isAi ? " The ones you marked “still learning” are saved to My words." : " The ones you're still learning will lead next time."}</p>
        <div class="flex flex-wrap gap-2 justify-center">
          ${isAi ? `<button id="flash-saveall" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2 rounded-lg font-medium text-sm">Save all to My words</button>` : ""}
          <button id="flash-restart" class="bg-white hover:border-sage-300 text-sage-600 border border-sage-200 px-5 py-2 rounded-lg font-medium text-sm">${isAi ? "New set" : "Go again"}</button>
        </div>
        <p id="flash-saveall-msg" class="text-xs text-sage-400 mt-3"></p>
      </div>
    `;
    area.querySelector("#flash-restart").addEventListener("click", () => {
      flashcardsView(app);
      if (!isAi) app.querySelector("#flash-start").click();
    });
    const saveAll = area.querySelector("#flash-saveall");
    if (saveAll) {
      saveAll.addEventListener("click", async () => {
        saveAll.disabled = true;
        saveAll.textContent = "Saving…";
        const msg = area.querySelector("#flash-saveall-msg");
        let n = 0;
        for (const c of fs.cards) {
          const r = await addPersonalWord({ word: c._en, es: c._es, pos: c._pos, tier: c._tier, theme: c._theme });
          if (r && r.added) n++;
        }
        saveAll.textContent = "Saved ✓";
        if (msg) msg.textContent = `${n} new word${n === 1 ? "" : "s"} added to My words.`;
      });
    }
    return;
  }
  const card = fs.cards[fs.idx];
  area.innerHTML = `
    <div class="bg-white rounded-2xl border border-sage-100 p-6">
      <div class="flex justify-between text-xs text-sage-400 mb-4">
        <span>Card ${fs.idx + 1} / ${fs.cards.length}</span>
      </div>
      <div id="flash-card" class="flash-card cursor-pointer mb-5" style="min-height:220px">
        <div class="flash-inner relative h-56">
          <div class="flash-face absolute inset-0 bg-gradient-to-br from-sage-50 to-sand-50 border border-sage-200 rounded-2xl flex items-center justify-center p-4">
            ${faceHTML(card.front, card.frontIsEnglish ? "" : card.emoji)}
          </div>
          <div class="flash-face flash-back absolute inset-0 bg-gradient-to-br from-sage-100 to-sage-50 border border-sage-300 rounded-2xl flex items-center justify-center p-4 overflow-auto">
            ${backHTML(card)}
          </div>
        </div>
      </div>
      <p class="text-center text-xs text-sage-400 mb-2" id="flip-hint">Tap card to flip</p>
      <p class="text-center mb-4">${speakButtonHTML(card.frontIsEnglish ? card.back : card.front, { label: "Hear it" })}</p>
      <div class="grid grid-cols-2 gap-3">
        <button id="wrong-btn" class="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 px-4 py-3 rounded-xl font-medium">
          ✗ Still learning
        </button>
        <button id="right-btn" class="bg-sage-100 hover:bg-sage-200 text-sage-700 border border-sage-300 px-4 py-3 rounded-xl font-medium">
          ✓ I knew it
        </button>
      </div>
    </div>
  `;
  const cardEl = app.querySelector("#flash-card");
  cardEl.addEventListener("click", () => {
    fs.flipped = !fs.flipped;
    cardEl.classList.toggle("flash-flipped");
    app.querySelector("#flip-hint").textContent = fs.flipped ? "Tap card to flip back" : "Tap card to flip";
  });
  app.querySelector("#right-btn").addEventListener("click", () => markCard(app, true));
  app.querySelector("#wrong-btn").addEventListener("click", () => markCard(app, false));
}

function markCard(app, known) {
  const fs = flashState;
  const card = fs.cards[fs.idx];
  if (!flashStats[card.key]) flashStats[card.key] = { correct: 0, wrong: 0 };
  if (known) flashStats[card.key].correct++;
  else flashStats[card.key].wrong++;
  // Personal words persist their Leitner schedule so known cards recede over
  // days (the "Anki" feel). Standard decks stay session-only.
  if (card._personal && card._id) {
    recordPersonalReview(card._id, known ? "known" : "learning");
  }
  // AI-generated card you DIDN'T know → save it to My words so it comes back in
  // the spaced-repetition rotation. De-duplicated server-side; fire-and-forget.
  if (card._aiSave && !known) {
    addPersonalWord({ word: card._en, es: card._es, pos: card._pos, tier: card._tier, theme: card._theme });
  }
  fs.idx++;
  fs.flipped = false;
  renderFlash(app);
}
